import { useCallback, useRef, useState } from 'react';

export interface SheetOpenRequest {
    /** 0 while closed; incremented on every open request. */
    openRequestId: number;
    isOpen: boolean;
    open: () => void;
    close: () => void;
}

/**
 * Owns the visibility of a bottom sheet as a monotonic request id rather than a boolean.
 *
 * A boolean cannot express "open it again" while it is already `true`: `setIsOpen(true)` on a
 * `true` value is a React no-op, so no render happens and the sheet's presentation effect never
 * re-runs. That silently swallows any press landing while the sheet is still animating — and if
 * the sheet tears itself down without reporting it (see `useBottomSheetPresentation`), the flag
 * stays `true` forever and the trigger is dead for the rest of the session (JSAD-7298).
 *
 * An incrementing id always changes, so every press is a distinct request the sheet can act on.
 *
 * Ids are strictly increasing and never reused: a sheet compares the request it is currently
 * serving against the latest one, so a reopen that recycled an earlier id would be mistaken for
 * the request already in flight and dropped.
 */
export const useSheetOpenRequest = (): SheetOpenRequest => {
    const [openRequestId, setOpenRequestId] = useState(0);
    const issuedRequests = useRef(0);

    const open = useCallback(() => {
        issuedRequests.current += 1;
        setOpenRequestId(issuedRequests.current);
    }, []);
    const close = useCallback(() => setOpenRequestId(0), []);

    return { openRequestId, isOpen: openRequestId > 0, open, close };
};

export default useSheetOpenRequest;
